import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useReportMutations } from 'hooks/useReport';
import { useSelector } from 'react-redux';

function CreateReportForm({ customer, initialData, isEdit, onCancel, onSuccess }) {
    const verifyReducer = useSelector((state) => state.verifyReducer);
    const { verifiedUser } = verifyReducer;
    // console.log('verifiedUser:', verifiedUser);

    const toast = useToast();
    const { create: createReport, update: updateReport, isPending } = useReportMutations();

    const [formData, setFormData] = useState({
        customer: customer?.id || '',
        customerEmail: customer?.email || '',
        salesperson: '',
        feedback: '',
        followUpNextStep: '',
        followUpDate: '',
        status: '',
        priority: '',
        images: [],
    });

    useEffect(() => {
        if (isEdit && initialData) {
            setFormData((prev) => ({
                ...prev,
                customerEmail: initialData.customerEmail || prev.customerEmail,
                feedback: initialData.feedback || '',
                followUpNextStep: initialData.followUpNextStep || '',
                followUpDate: initialData.followUpDate
                    ? new Date(initialData.followUpDate).toISOString().split('T')[0]
                    : '',
                status: initialData.status || '',
                priority: initialData.priority || '',
                images: [],
            }));
        }
    }, [initialData, isEdit, customer]);

    useEffect(() => {
        if (verifiedUser?.data?.user?.attributes) {
            const userAttributes = verifiedUser.data.user.attributes;

            const firstName =
                userAttributes?.user_information?.first_name || userAttributes?.first_name || '';

            const lastName =
                userAttributes?.user_information?.last_name || userAttributes?.last_name || '';

            const fullName = `${firstName} ${lastName}`.trim();

            if (fullName) {
                setFormData((prev) => ({
                    ...prev,
                    salesperson: fullName,
                }));
            }
        }
    }, [verifiedUser]);

    const statusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold'];
    const priorityOptions = ['Low', 'Medium', 'High', 'Urgent'];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        setFormData({
            ...formData,
            images: files,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const customerId = customer?.contact_id;
        const customerName = customer?.customer_name || customer?.contact_name || '';
        const salespersonEmail = verifiedUser?.data?.user?.email || '';

        const fd = new FormData();
        if (isEdit && initialData?.id) {
            fd.append('id', initialData.id);
        }
        fd.append('customer', customerId);
        fd.append('customerName', customerName);
        fd.append('customerEmail', formData.customerEmail);
        fd.append('salesperson', formData.salesperson);
        fd.append('salespersonEmail', salespersonEmail);
        fd.append('feedback', formData.feedback);
        fd.append('followUpNextStep', formData.followUpNextStep);
        fd.append('followUpDate', formData.followUpDate);
        fd.append('status', formData.status);
        fd.append('priority', formData.priority);
        formData.images.forEach((file) => {
            fd.append('images', file);
        });

        try {
            const result = await (isEdit ? updateReport(fd) : createReport(fd));

            if (result && result.success) {
                if (!isEdit) {
                    setFormData({
                        customer: customer?.id || '',
                        customerEmail: customer?.email || '',
                        salesperson: formData.salesperson,
                        feedback: '',
                        followUpNextStep: '',
                        followUpDate: '',
                        status: '',
                        priority: '',
                        images: [],
                    });
                }

                if (onSuccess) onSuccess();

                setTimeout(() => {
                    toast({
                        title: isEdit ? 'Report Updated' : 'Report Created',
                        description: `Your report has been ${
                            isEdit ? 'updated' : 'submitted'
                        } successfully.`,
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                }, 100);
            } else {
                if (!result || !result.error) {
                    toast({
                        title: 'Error',
                        description: `Failed to ${
                            isEdit ? 'update' : 'submit'
                        } report. Please try again.`,
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            console.error('Unexpected error:', error);
            toast({
                title: 'Error',
                description: 'An unexpected error occurred. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box bg="white" borderRadius="lg" p={1}>
            <form onSubmit={handleSubmit}>
                <VStack spacing={3} align="stretch">
                    <FormControl isRequired>
                        <FormLabel color="#667085" fontSize="sm" mb={1}>
                            Customer Name
                        </FormLabel>
                        <Input
                            size="sm"
                            value={customer?.customer_name || customer?.contact_name || ''}
                            isReadOnly
                            bg="#F9FAFB"
                            borderRadius="8px"
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel color="#667085" fontSize="sm" mb={1}>
                            Customer Email
                        </FormLabel>
                        <Input
                            size="sm"
                            type="email"
                            name="customerEmail"
                            value={formData.customerEmail}
                            onChange={handleInputChange}
                            placeholder="Enter customer email"
                            borderRadius="8px"
                            isReadOnly={!!formData.customerEmail}
                            bg={formData.customerEmail ? '#F9FAFB' : 'white'}
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel color="#667085" fontSize="sm" mb={1}>
                            Salesperson Name
                        </FormLabel>
                        <Input
                            size="sm"
                            type="text"
                            name="salesperson"
                            value={formData.salesperson}
                            isReadOnly
                            bg="#F9FAFB"
                            borderRadius="8px"
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel color="#667085" fontSize="sm" mb={1}>
                            Feedback
                        </FormLabel>
                        <Textarea
                            size="sm"
                            name="feedback"
                            value={formData.feedback}
                            onChange={handleInputChange}
                            placeholder="Enter feedback"
                            borderRadius="8px"
                            rows={3}
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel color="#667085" fontSize="sm" mb={1}>
                            Follow-up Next Step
                        </FormLabel>
                        <Textarea
                            size="sm"
                            name="followUpNextStep"
                            value={formData.followUpNextStep}
                            onChange={handleInputChange}
                            placeholder="Enter follow-up next step"
                            borderRadius="8px"
                            rows={2}
                        />
                    </FormControl>

                    <Flex gap={3}>
                        <FormControl isRequired flex={1}>
                            <FormLabel color="#667085" fontSize="sm" mb={1}>
                                Follow-up Date
                            </FormLabel>
                            <Input
                                size="sm"
                                type="date"
                                name="followUpDate"
                                value={formData.followUpDate}
                                onChange={handleInputChange}
                                borderRadius="8px"
                            />
                        </FormControl>

                        <FormControl isRequired flex={1}>
                            <FormLabel color="#667085" fontSize="sm" mb={1}>
                                Status
                            </FormLabel>
                            <Select
                                size="sm"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                placeholder="Select status"
                                borderRadius="8px">
                                {statusOptions.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Flex>

                    <Flex gap={3}>
                        <FormControl isRequired flex={1}>
                            <FormLabel color="#667085" fontSize="sm" mb={1}>
                                Priority
                            </FormLabel>
                            <Select
                                size="sm"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                placeholder="Select priority"
                                borderRadius="8px">
                                {priorityOptions.map((priority) => (
                                    <option key={priority} value={priority}>
                                        {priority}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl flex={1}>
                            <FormLabel color="#667085" fontSize="sm" mb={1}>
                                Upload Images
                            </FormLabel>
                            <Input
                                size="sm"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                borderRadius="8px"
                                pt={1}
                            />
                        </FormControl>
                    </Flex>

                    {formData.images.length > 0 && (
                        <Text fontSize="xs" color="#667085">
                            {formData.images.length} file(s) selected
                        </Text>
                    )}

                    <Flex gap={3} pt={3} justifyContent="flex-end">
                        <Button
                            type="button"
                            size="sm"
                            color="#1b3b5f"
                            bg="#B8C1D380"
                            borderRadius="8px"
                            px={6}
                            fontWeight="medium"
                            onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            color="white"
                            bg="#1b3b5f"
                            borderRadius="8px"
                            px={6}
                            fontWeight="medium"
                            isLoading={isPending}
                            loadingText={isEdit ? 'Updating' : 'Submitting'}>
                            {isEdit ? 'Update Report' : 'Submit Report'}
                        </Button>
                    </Flex>
                </VStack>
            </form>
        </Box>
    );
}

export default CreateReportForm;
